"use client";

import { useEffect } from "react";
import { Provider, useStore } from "react-redux";
import { store } from "./store";
import {
  rehydrateAuth,
  mergeAuthUserProfile,
  resetAuthState,
} from "./slices/loginRegisterSlice";
import { parseLocalStorage, setLocalStorage, clearRememberedLogin } from "../utils/localStorage";
import { getAuthToken, hydrateAccessTokenFromStorage, clearAuthData } from "../utils/authUtils";
import { ToastProvider } from "@/components/Toast/ToastContext";
import { ModalProvider } from "@/components/Modals/ModalContext";
import WalletShell from "@/components/providers/WalletShell";
import apiService, {
  initProactiveAccessTokenRefresh,
  refreshAccessTokenSilently,
} from "@/utils/apiService";

const AUTH_USER_UPDATED_EVENT = "autobot-auth-user-updated";

function AuthRehydrator({ children }) {
  const store = useStore();
  useEffect(() => {
    clearRememberedLogin();
    let cancelled = false;
    try {
      const userData = hydrateAccessTokenFromStorage();
      if (userData && userData.user) {
        store.dispatch(rehydrateAuth({ user: userData.user }));
      }
      if (userData?.user && typeof window !== "undefined") {
        (async () => {
          if (!getAuthToken()) {
            try {
              await refreshAccessTokenSilently();
            } catch {
              if (!cancelled) {
                clearAuthData({ skipRedirect: true });
                store.dispatch(resetAuthState());
              }
              return;
            }
          }
          if (!cancelled) initProactiveAccessTokenRefresh();
        })();
      }
    } catch {
      // ignore
    }
    return () => {
      cancelled = true;
    };
  }, [store]);

  useEffect(() => {
    const handler = (e) => {
      const u = e.detail?.user;
      if (u && typeof u === "object") {
        store.dispatch(mergeAuthUserProfile(u));
      }
    };
    window.addEventListener(AUTH_USER_UPDATED_EVENT, handler);
    return () => window.removeEventListener(AUTH_USER_UPDATED_EVENT, handler);
  }, [store]);

  /** One-shot reconcile voucher snapshot vs Redux/LS (fixes stale offerActive after backend rollout). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ud = parseLocalStorage("autobot_user");
        if (!ud?.user || !getAuthToken()) return;
        const p = ud.user.firstRechargePromo;
        if (p?.consumed) return;
        if (p?.claimed) return;
        if (p?.offerActive !== true) return;
        const res = await apiService.post(
          "/users/me/first-recharge-voucher",
          {},
          { requiresAuth: true },
        );
        const nextPromo = res?.data?.firstRechargePromo;
        const nextDiscount = res?.data?.firstRechargeDiscount;
        if (cancelled || !nextPromo) return;
        const fresh = parseLocalStorage("autobot_user");
        if (fresh?.user) {
          fresh.user.firstRechargePromo = nextPromo;
          if (nextDiscount != null) {
            fresh.user.firstRechargeDiscount = nextDiscount;
          }
          setLocalStorage("autobot_user", JSON.stringify(fresh));
          store.dispatch(
            mergeAuthUserProfile({
              firstRechargePromo: nextPromo,
              ...(nextDiscount != null
                ? { firstRechargeDiscount: nextDiscount }
                : {}),
            }),
          );
        }
      } catch {
        /* offline / 401 — ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [store]);

  return children;
}

export function ReduxProvider({ children }) {
  return (
    <Provider store={store}>
      <AuthRehydrator>
        <ToastProvider>
          <WalletShell>
            <ModalProvider>{children}</ModalProvider>
          </WalletShell>
        </ToastProvider>
      </AuthRehydrator>
    </Provider>
  );
}
